import React, { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "reactstrap";
import { connect } from "react-redux";
import { firebaseConnect, isLoaded } from "react-redux-firebase";
import { get } from "lodash";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import EditGivingSite from "../../../onboardingSteps/brandSetup/editGivingSite";
import {
  BrandSetupWizardContext,
  CustomizeBackOverride,
} from "../../../onboardingSteps/brandSetup/context";
import { saveBrandSetupData } from "../../../../../../utils/firebase/brandSetup";
import {
  customizationHasUsableKeys,
  writeHydratedCustomization,
} from "../../../../../../utils/firebase/customization";
import { fetchDonorSiteCustomizationFromYml } from "../../../../../../utils/fetch";
import LoadingSpinner from "../../../../../../toolkit/loadingSpinner";
import ErrorDisplay from "../../../../../../toolkit/errorDisplay";
import SnackbarNotification from "../../../../../../toolkit/snackbarNotification";
import { useSnackbar } from "../../../../../../hooks/useSnackbar";
import WizardBreadcrumb from "../../../onboardingSteps/common/WizardBreadcrumb";

interface Props extends ServiceCallerProps {
  charityId: string;
  customizationLoaded: boolean;
  customization: Record<string, unknown> | null;
  onExit?: () => void;
}

const WebsiteMaintainPage: React.FC<Props> = ({
  charityId,
  customizationLoaded,
  customization,
  config,
  serviceAccessToken,
  onExit,
}) => {
  const { snackbarProps, showSnackbar } = useSnackbar();
  const [hydrating, setHydrating] = useState(true);
  const [hydrateError, setHydrateError] = useState<string | null>(null);
  const [publishEnabled, setPublishEnabled] = useState(false);
  const [customizeBackOverride, setCustomizeBackOverride] =
    useState<CustomizeBackOverride | null>(null);

  useEffect(() => {
    if (!customizationLoaded || !charityId) {
      return;
    }

    let cancelled = false;

    const hydrate = async () => {
      if (customizationHasUsableKeys(customization)) {
        if (!cancelled) {
          setPublishEnabled(true);
          setHydrateError(null);
          setHydrating(false);
        }
        return;
      }

      if (!config?.adminServiceUrl || !serviceAccessToken) {
        if (!cancelled) {
          setPublishEnabled(false);
          setHydrating(false);
        }
        return;
      }

      try {
        const dto = await fetchDonorSiteCustomizationFromYml(
          config.adminServiceUrl,
          charityId,
          serviceAccessToken
        );
        if (cancelled) {
          return;
        }
        if (!customizationHasUsableKeys(dto)) {
          setPublishEnabled(false);
          setHydrateError(null);
          setHydrating(false);
          return;
        }
        await writeHydratedCustomization(charityId, dto);
        await saveBrandSetupData(charityId, {
          setupMethod: "manual",
          progressTracker: "customization_started",
          status: "completed",
        });
        setPublishEnabled(true);
        setHydrateError(null);
      } catch (error) {
        if (!cancelled) {
          setPublishEnabled(false);
          setHydrateError(null);
        }
      } finally {
        if (!cancelled) {
          setHydrating(false);
        }
      }
    };

    hydrate();

    return () => {
      cancelled = true;
    };
  }, [
    charityId,
    customizationLoaded,
    customization,
    config?.adminServiceUrl,
    serviceAccessToken,
  ]);

  const contextValue = useMemo(
    () => ({
      usesExternalGivingPage: false,
      customizeStepIndex: 0,
      onExit,
      showSnackbar,
      customizeBackOverride,
      setCustomizeBackOverride,
    }),
    [onExit, showSnackbar, customizeBackOverride]
  );

  return (
    <>
      <Container>
        <Row>
          <Col>
            <WizardBreadcrumb onBack={onExit} showBreadcrumb />
            {hydrating || !customizationLoaded ? (
              <div className="mt-5">
                <LoadingSpinner
                  title="Loading website"
                  message="Please wait while we load your ViewSpark website settings."
                />
              </div>
            ) : hydrateError ? (
              <ErrorDisplay
                errorMessage={hydrateError}
                title="Customize website"
                showBack={!!onExit}
                onBack={onExit}
              />
            ) : (
              <BrandSetupWizardContext.Provider value={contextValue}>
                <EditGivingSite
                  returnToCustomizeHub
                  publishDisabled={!publishEnabled}
                  onExit={onExit}
                />
              </BrandSetupWizardContext.Provider>
            )}
          </Col>
        </Row>
      </Container>
      <SnackbarNotification
        {...snackbarProps}
        position={{ vertical: "top", horizontal: "center" }}
      />
    </>
  );
};

const mapStateToProps = (state: any) => {
  const charityId = state.user?.currentCharity?.id || "";
  const customizationLoaded = Boolean(
    charityId &&
      isLoaded(state.firebase.data[charityId]) &&
      isLoaded(get(state.firebase, `data.${charityId}.charitySetup.customization`))
  );
  return {
    charityId,
    customizationLoaded,
    customization: customizationLoaded
      ? get(state.firebase, `data.${charityId}.charitySetup.customization`, null)
      : null,
  };
};

export default connect(mapStateToProps)(
  firebaseConnect((props: any) => {
    const charityId = props.charityId;
    return charityId ? [`${charityId}/charitySetup/customization`] : [];
  })(serviceCaller(WebsiteMaintainPage))
);
